import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import { useMemo } from 'react';
import {
  ConfigPaymentScheduleRow,
  ConfigPaymentScheduleValues,
  blockNegativeNumberKeys,
  formatPaymentDueLabel,
  getConfigPaymentScheduleRows,
  getDaysOrderWarnings,
  sanitizeNonNegativeIntInput,
} from './configPaymentScheduleUtils';

export type ConfigPaymentScheduleSectionProps = {
  values: ConfigPaymentScheduleValues;
  onChange: (field: keyof ConfigPaymentScheduleValues, value: unknown) => void;
  hasError?: boolean;
  errors?: Partial<Record<keyof ConfigPaymentScheduleValues, string>>;
};

const displayValue = (value: unknown) => (value == null ? '' : String(value));

const parsePercent = (value: unknown): number => {
  if (value === '' || value == null) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const computeTotalPercent = (values: ConfigPaymentScheduleValues): number =>
  getConfigPaymentScheduleRows().reduce((sum, row) => sum + parsePercent(values[row.percentField]), 0);

const compactFieldSx = {
  '& .MuiInputBase-root': {
    height: 32,
    fontSize: '0.875rem',
    fontVariantNumeric: 'tabular-nums',
    bgcolor: 'background.paper',
  },
  '& .MuiInputBase-input': { py: 0.5, px: 1 },
  '& .MuiFormHelperText-root': { mx: 0, mt: 0.25, lineHeight: 1.2, fontSize: '0.7rem' },
};

const AllocatedPercentChip = ({ value }: { value: number }) => {
  const complete = value === 100;
  const label = (strings.payment_schedule_percent_allocated || '{percent}% allocated').replace(
    '{percent}',
    String(value),
  );

  return (
    <Chip
      size="small"
      label={label}
      icon={
        complete ? (
          <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />
        ) : (
          <WarningAmberOutlinedIcon sx={{ fontSize: '14px !important' }} />
        )
      }
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        ...(complete
          ? { bgcolor: '#E6F4EA', color: '#137333', '& .MuiChip-icon': { color: '#137333' } }
          : {
              bgcolor: alpha('#ED6C02', 0.12),
              color: 'warning.dark',
              '& .MuiChip-icon': { color: 'warning.main' },
            }),
      }}
    />
  );
};

export const ConfigPaymentScheduleSection = ({
  values,
  onChange,
  hasError = false,
  errors = {},
}: ConfigPaymentScheduleSectionProps) => {
  const theme = useTheme();
  const rows = getConfigPaymentScheduleRows();
  const totalPercent = useMemo(() => computeTotalPercent(values), [values]);
  const daysOrderWarnings = useMemo(() => getDaysOrderWarnings(values), [values]);

  const rowGrid = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1.4fr) 88px minmax(0, 1fr)' },
    alignItems: 'start',
    columnGap: 1.5,
    rowGap: { xs: 0.75, sm: 0 },
  };

  const dueColumnSx = { pl: { xs: 0, sm: 2.5 } };
  const tableBorder = `1px solid ${alpha(theme.palette.divider, 0.35)}`;

  const renderPercentField = (row: ConfigPaymentScheduleRow) => {
    const percentField = row.percentField;
    const percentError = errors[percentField];
    const showPercentError = hasError && !!percentError;

    return (
      <TextField
        size="small"
        type="text"
        hiddenLabel
        placeholder="0"
        value={displayValue(values[percentField])}
        onChange={e => onChange(percentField, sanitizeNonNegativeIntInput(e.target.value, 100))}
        onKeyDown={blockNegativeNumberKeys}
        error={showPercentError}
        helperText={showPercentError ? percentError : undefined}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'right' } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }}>
              <Typography color="text.secondary">%</Typography>
            </InputAdornment>
          ),
        }}
        sx={{ ...compactFieldSx, width: '100%' }}
      />
    );
  };

  const renderDueField = (row: ConfigPaymentScheduleRow) => {
    if (row.dueType === 'on_confirmation') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.65, lineHeight: 1.3 }}>
          {strings.payment_schedule_on_confirmation}
        </Typography>
      );
    }

    const daysField = row.daysField;
    const fieldError = errors[daysField];
    const showError = hasError && !!fieldError;

    const daysValue = values[daysField];
    const dueLabel = formatPaymentDueLabel(daysValue);
    const showOrderWarning =
      daysField === 'paymentSlotTwoBeforeDays' || daysField === 'fullPaymentBeforeDays'
        ? !!daysOrderWarnings[daysField]
        : false;
    const warningMessage =
      daysField === 'paymentSlotTwoBeforeDays'
        ? strings.payment_schedule_days_order_warning_second
        : strings.payment_schedule_days_order_warning_final;

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
        <TextField
          size="small"
          type="text"
          hiddenLabel
          placeholder="0"
          value={displayValue(daysValue)}
          onChange={e => onChange(daysField, sanitizeNonNegativeIntInput(e.target.value, 100))}
          onKeyDown={blockNegativeNumberKeys}
          error={showError}
          helperText={showError ? fieldError : undefined}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center' } }}
          sx={{ ...compactFieldSx, width: 64, flexShrink: 0 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.65, lineHeight: 1.3 }}>
          {dueLabel}
        </Typography>
        {showOrderWarning && (
          <Tooltip title={warningMessage} placement="top">
            <WarningAmberOutlinedIcon
              sx={{ color: 'warning.main', fontSize: 18, mt: 0.7, flexShrink: 0, cursor: 'default' }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 1, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: `1px solid ${theme.palette.primary.main}`,
          mb: 1,
          pb: 1,
        }}>
        <Typography color="primary" fontWeight="bold" sx={{ lineHeight: 1.35 }}>
          {strings.payment_schedule_title}:
        </Typography>
        <AllocatedPercentChip value={totalPercent} />
      </Box>

      <Box
        sx={{
          borderRadius: 1,
          border: tableBorder,
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            ...rowGrid,
            display: { xs: 'none', sm: 'grid' },
            px: 1.5,
            py: 0.6,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderBottom: tableBorder,
          }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            {strings.payment_schedule_column_milestone}
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.secondary" textAlign="right">
            {strings.payment_schedule_column_percent}
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={dueColumnSx}>
            {strings.payment_schedule_column_due}
          </Typography>
        </Box>

        <Stack spacing={0}>
          {rows.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                ...rowGrid,
                px: 1.5,
                py: { xs: 0.85, sm: 0.75 },
                ...(index < rows.length - 1
                  ? { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.25)}` }
                  : {}),
              }}>
              <Typography variant="body2" fontWeight={600} sx={{ py: { sm: 0.65 }, lineHeight: 1.3 }}>
                {row.label}
              </Typography>
              <Box>{renderPercentField(row)}</Box>
              <Box sx={dueColumnSx}>{renderDueField(row)}</Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default ConfigPaymentScheduleSection;
